import bodyScrollLock from '../utils/bodyScrollLock'
import onAnimationEnd from '../utils/onAnimationEnd'
import { isNil } from 'lodash'

enum PanelSide {
  left = 'Left',
  right = 'Right',
}

const JS_PREFIX = 'prp'

class Panel {
  activePanel: HTMLElement | null
  activePanelContent: HTMLElement | null
  backdrop: HTMLElement | null
  panelSide: PanelSide.left | PanelSide.right
  isOpen: boolean

  constructor(panelId?: string) {
    this.activePanel = !isNil(panelId) ? document.querySelector(`[panel-id="${panelId}"]`) : null
    this.activePanelContent = null
    this.backdrop = null
    this.panelSide = PanelSide.left
    this.isOpen = false

    this.addOpenListeners()
  }

  public addOpenListeners = (): void => {
    const openTriggers = Array.from(document.getElementsByClassName(`${JS_PREFIX}-open-panel`))
    for (const el of openTriggers) {
      const trigger = <HTMLElement>el
      trigger.addEventListener('click', this.open)
    }
  }

  public addCloseListeners = (): void => {
    const closeTriggers = Array.from(document.getElementsByClassName(`${JS_PREFIX}-close-panel`))
    for (const el of closeTriggers) {
      const trigger = <HTMLElement>el
      trigger.addEventListener('click', this.close)
    }
  }

  public removeCloseListeners = (): void => {
    const closeTriggers = Array.from(document.getElementsByClassName(`${JS_PREFIX}-close-panel`))
    for (const el of closeTriggers) {
      const trigger = <HTMLElement>el
      trigger.removeEventListener('click', this.close)
    }
  }

  public open = (e: MouseEvent): void => {
    // capture event
    const _this = e.currentTarget

    // get panel id
    const panelId = _this instanceof HTMLElement ? _this.getAttribute('panel-target') : null

    // set active panel
    this.activePanel = document.querySelector(`[panel-id="${panelId}"]`)

    // grab the content div from parent
    this.activePanelContent = <HTMLElement>this.activePanel?.children[0]

    // set panel side
    this.panelSide = this.activePanel?.getAttribute('panel-side') === 'left' ? PanelSide.left : PanelSide.right

    // mount backdrop (body scroll will be disabled after backdrop fade-in completes)
    this.mountBackdrop()

    // set close listeners
    this.addCloseListeners()

    // show panel
    this.showPanel()

    // a11y: update aria on trigger and panel
    if (_this instanceof HTMLElement) {
      _this.setAttribute('aria-expanded', 'true')
      const controls = _this.getAttribute('aria-controls')
      if (controls) {
        const panel = document.getElementById(controls)
        panel?.setAttribute('aria-hidden', 'false')
      }
    }
  }

  public close = (): void => {
    // enable body scrolling
    bodyScrollLock.enableBodyScroll()

    // hide panel
    this.hidePanel()

    // destroy backdrop
    this.destroyBackDrop()

    // remove close listeners
    this.removeCloseListeners()

    // a11y: update aria on any open trigger and active panel
    const triggers = Array.from(document.getElementsByClassName(`${JS_PREFIX}-open-panel`))
    for (const el of triggers) {
      const trigger = el as HTMLElement
      const target = trigger.getAttribute('panel-target')
      if (this.activePanel && target === this.activePanel.getAttribute('panel-id')) {
        trigger.setAttribute('aria-expanded', 'false')
        const controls = trigger.getAttribute('aria-controls')
        if (controls) {
          const panel = document.getElementById(controls)
          panel?.setAttribute('aria-hidden', 'true')
        }
      }
    }
  }

  private getSlideClass = (direction: 'in' | 'out'): string => {
    if (this.panelSide === PanelSide.right) {
      return direction === 'in' ? 'animate-slideInRight' : 'animate-slideOutRight'
    }
    return direction === 'in' ? 'animate-slideInLeft' : 'animate-slideOutLeft'
  }

  private showPanel = () => {
    const slideInAnimation = this.getSlideClass('in')

    this.activePanel?.classList.remove('hidden', '-z-10')
    this.activePanel?.classList.add('z-1060')
    this.activePanelContent?.classList.add(slideInAnimation)
    this.isOpen = true
  }

  private hidePanel = () => {
    const slideInAnimation = this.getSlideClass('in')
    const slideOutAnimation = this.getSlideClass('out')

    // animate out then set panel to null
    this.activePanelContent?.classList.remove(slideInAnimation)
    if (!isNil(this.activePanelContent)) {
      onAnimationEnd(this.activePanelContent, slideOutAnimation).then(() => {
        this.activePanel?.classList.remove('z-1060')
        this.activePanel?.classList.add('hidden', '-z-10')
        this.activePanel = null
        this.isOpen = false
      })
    }
  }

  private mountBackdrop = (): void => {
    // bail if backdrop exists
    if (!isNil(this.backdrop)) {
      return
    }

    this.backdrop = document.createElement('div')
    this.backdrop.id = 'modal-backdrop'
    const backdropClasses = [
      'fixed',
      'inset-0',
      'z-1040',
      'w-screen',
      'h-screen',
      'bg-black/90',
      `${JS_PREFIX}-close-panel`,
    ]
    this.backdrop.classList.add(...backdropClasses)
    document.body.appendChild(this.backdrop)

    // Run fade-in animation and disable scroll when it completes
    if (this.backdrop) {
      onAnimationEnd(this.backdrop, 'animate-fadeIn').then(() => {
        bodyScrollLock.disableBodyScroll()
      })
    }
  }

  private destroyBackDrop = (): Promise<void> => {
    // bail if backdrop doesn't exist
    if (isNil(this.backdrop)) {
      return Promise.resolve()
    }

    // remove fade in class
    this.backdrop.classList.remove('animate-fadeIn')

    // animate out then destroy
    return onAnimationEnd(this.backdrop, 'animate-fadeOut').then(() => {
      this.backdrop?.remove()
      this.backdrop = null
    })
  }
}

export default Panel
